

import React, { useState, useEffect } from 'react';
import { Menu, X, Calendar, Sparkles, ChevronDown, Megaphone, Headphones, Gift, TrendingUp, MessageSquare, Store, Users, Target, LineChart, Layers } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [featuresHovered, setFeaturesHovered] = useState(false);
  const [featuresTimeout, setFeaturesTimeout] = useState(null);
  const [solutionsHovered, setSolutionsHovered] = useState(false);
  const [solutionsTimeout, setSolutionsTimeout] = useState(null);
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isClientPage = location.pathname === '/client';
  const isBlogPage = location.pathname === '/blog';
  const isBlogDetailsPage = location.pathname.startsWith('/blog/');
  const isEnterprisePage = location.pathname === '/agencies';
  const isD2cPage = location.pathname === '/solutions-d2c';
  const isPaidAdsPage = location.pathname === '/solutions/paid-ads';
  const isEngagementPage = location.pathname === '/' || location.pathname === '/solutions-d2c';
  const HOW_IT_WORKS_HASH = '#how-it-works';
  const INTEGRATIONS_HASH = '#integrations';

  const logoSrc = '/kolanut_logo.png';

  const scrollToSection = (hash, menuWasOpen = false) => {
    const scrollDelay = menuWasOpen ? 300 : 0;
    setTimeout(() => {
      const targetId = hash.replace('#', '');
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, scrollDelay);
  };
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle features dropdown with delay
  const handleFeaturesMouseEnter = () => {
    if (featuresTimeout) {
      clearTimeout(featuresTimeout);
      setFeaturesTimeout(null);
    }
    setFeaturesHovered(true);
  };

  const handleFeaturesMouseLeave = () => {
    const timeout = setTimeout(() => {
      setFeaturesHovered(false);
    }, 150); // 150ms delay before closing
    setFeaturesTimeout(timeout);
  };

  const handleSolutionsMouseEnter = () => {
    if (solutionsTimeout) {
      clearTimeout(solutionsTimeout);
      setSolutionsTimeout(null);
    }
    setSolutionsHovered(true);
  };

  const handleSolutionsMouseLeave = () => {
    const timeout = setTimeout(() => {
      setSolutionsHovered(false);
    }, 150);
    setSolutionsTimeout(timeout);
  };

  // Handle mobile features toggle
  const handleMobileFeaturesToggle = () => {
    setMobileFeaturesOpen(!mobileFeaturesOpen);
  };

  const handleMobileSolutionsToggle = () => {
    setMobileSolutionsOpen(!mobileSolutionsOpen);
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const featuresDropdown = document.getElementById('features-dropdown');
      const featuresButton = document.getElementById('features-button');
      const solutionsDropdown = document.getElementById('solutions-dropdown');
      const solutionsButton = document.getElementById('solutions-button');
      
      if (featuresDropdown && featuresButton && 
          !featuresDropdown.contains(event.target) && 
          !featuresButton.contains(event.target)) {
        setFeaturesHovered(false);
      }

      if (solutionsDropdown && solutionsButton &&
          !solutionsDropdown.contains(event.target) &&
          !solutionsButton.contains(event.target)) {
        setSolutionsHovered(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (featuresTimeout) {
        clearTimeout(featuresTimeout);
      }
      if (solutionsTimeout) {
        clearTimeout(solutionsTimeout);
      }
    };
  }, [featuresTimeout, solutionsTimeout]);

  // Close mobile dropdowns when main menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setMobileFeaturesOpen(false);
      setMobileSolutionsOpen(false);
    }
  }, [isMenuOpen]);

  // Handle navigation with proper routing
  const handleNavigation = (href, e) => {
    e.preventDefault();
    
    // Close mobile menu first if open
    const menuWasOpen = isMenuOpen;
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    
    // Handle hash links (in-page sections)
    if (href.startsWith('#')) {
      if (href === HOW_IT_WORKS_HASH) {
        if (isEngagementPage) {
          scrollToSection(href, menuWasOpen);
        } else {
          navigate('/', { state: { scrollTo: href } });
        }
        return;
      }

      if (href === INTEGRATIONS_HASH) {
        if (location.pathname === '/') {
          scrollToSection(href, menuWasOpen);
        } else {
          navigate('/', { state: { scrollTo: href } });
        }
        return;
      }

      if (!isPaidAdsPage) {
        navigate('/solutions/paid-ads', { state: { scrollTo: href } });
      } else {
        scrollToSection(href, menuWasOpen);
      }
    } else {
      // Regular navigation (non-hash links)
      navigate(href);
    }
  };

  const handleSolutionsNavigation = (href, e) => {
    e.preventDefault();

    if (href === location.pathname) {
      setSolutionsHovered(false);
      setMobileSolutionsOpen(false);
      if (solutionsTimeout) {
        clearTimeout(solutionsTimeout);
        setSolutionsTimeout(null);
      }
      return;
    }

    setSolutionsHovered(false);
    setMobileSolutionsOpen(false);
    handleNavigation(href, e);
  };
  
  // Base menu items for the coach page with updated href links
  const coachMenuItems = [
    // { title: 'About', href: '/about' },
    { title: 'How it works', href: HOW_IT_WORKS_HASH },
    { title: 'Integrations', href: INTEGRATIONS_HASH },
    // { title: 'Blog', href: '/blog' },
    { title: 'Thesis', href: '/kolanut-thesis' },
    { title: 'Pricing', href: '/pricing' }
  ];

  // Solutions dropdown items
  const solutionsItems = [
    // {
    //   title: 'D2C Brands',
    //   href: '/solutions-d2c',
    //   description: 'Spot customers going quiet and bring them back',
    //   icon: Store,
    //   gradient: 'from-orange-500 to-amber-500',
    // },
    {
      title: 'SaaS Companies',
      href: '/',
      description: 'See at-risk and silent accounts, then reach them before they churn',
      icon: Layers,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Paid Ads',
      href: '/solutions/paid-ads',
      description: 'AI marketing co-pilot for campaigns, cross-platform ads, and growth on autopilot',
      icon: Megaphone,
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      title: 'Agencies',
      href: '/agencies',
      description: 'Orchestrate strategy, content, and campaigns across every client brand',
      icon: Users,
      gradient: 'from-gray-700 to-gray-900',
    },
  ];

  // Features dropdown items
  const featuresItems = [
    { 
      title: 'AI Strategy Agent', 
      href: '/features/visibility-engagement', 
      description: 'Market intel & competitive insights',
      icon: TrendingUp,
      gradient: 'from-indigo-500 to-purple-500'
    },
    { 
      title: 'Customer Support', 
      href: '/features/customer-happiness', 
      description: '24/7 support that scales',
      icon: Headphones,
      gradient: 'from-green-500 to-emerald-500'
    },
    { 
      title: 'Auto Branded Assets', 
      href: '/features/auto-branded-assets', 
      description: 'Generate on-brand content instantly',
      icon: Layers,
      gradient: 'from-violet-500 to-purple-500'
    },
    { 
      title: 'Referrals & Gift Cards', 
      href: '/features/referral', 
      description: 'Turn customers into revenue',
      icon: Gift,
      gradient: 'from-pink-500 to-rose-500'
    },
   
    { 
      title: 'Predictable Growth', 
      href: '/features/predictable-growth', 
      description: 'Smart campaigns, real results',
      icon: LineChart,
      gradient: 'from-blue-500 to-cyan-500'
    },
    { 
      title: 'Customer Insights', 
      href: '/features/customer-insight', 
      description: 'Feedback that drives action',
      icon: MessageSquare,
      gradient: 'from-purple-500 to-violet-500',
      isNew: true
    }
  ];
  
  // Menu items for the client page
  const clientMenuItems = [
    { title: 'For Coaches', href: '/' },
    { title: 'Highlights', href: '#highlights' },
    { title: 'Testimonials', href: '#testimonials' }
  ];
  
  // Choose the appropriate menu items based on the current page
  const menuItems = isClientPage ? clientMenuItems : coachMenuItems;

  // Handle scroll to section after navigation
  useEffect(() => {
    if (location.state?.scrollTo) {
      const targetId = location.state.scrollTo.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      // Clear the state to prevent re-scrolling
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-[#c4ffa0]/20' 
        : 'bg-white/90 backdrop-blur-md border-b border-gray-100'
    }`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Left: Logo + Desktop nav (grouped so nav sits right after logo) */}
          <div className="flex items-center flex-shrink-0 min-w-0">
            {/* Logo and Brand */}
            <div className="flex items-center flex-shrink-0">
              <a href="/" className="flex items-center group">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#c4ffa0]/20 to-[#c4ffa0]/10 p-1.5">
                  <img
                    src={logoSrc}
                    alt="Kolanut"
                    className="h-12 w-12 object-contain rounded-2xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 animate-wiggle"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/30 to-green-500/20 opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-2xl"></div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-2xl opacity-0 group-hover:opacity-20 blur-sm transition-all duration-500"></div>
                </div>
                <div className="ml-3 flex flex-col">
                  <span className="text-2xl font-bold tracking-tight text-gray-900 group-hover:text-gray-700 transition-colors duration-300">
                    Kolanut
                  </span>
                  {isEnterprisePage && (
                    <span className="text-xs font-medium text-gray-600 group-hover:text-gray-500 transition-colors duration-300 -mt-1">
                      for Agencies
                    </span>
                  )}
                  {isD2cPage && (
                    <span className="text-xs font-medium text-gray-600 group-hover:text-gray-500 transition-colors duration-300 -mt-1">
                      for D2C Brands
                    </span>
                  )}
                </div>
              </a>
            </div>

            {/* Desktop Navigation - left aligned, right after logo */}
            <nav className="hidden md:flex items-center ml-6 flex-shrink-0">
            <div className="flex items-center space-x-6">
              {/* About */}
              {/* <a 
                href="/about" 
                onClick={(e) => handleNavigation('/about', e)}
                className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
              >
                About
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
              </a> */}
              
              {/* How it works */}
              <a 
                href={HOW_IT_WORKS_HASH}
                onClick={(e) => handleNavigation(HOW_IT_WORKS_HASH, e)}
                className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
              >
                How it works
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
              </a>

              <a
                href={INTEGRATIONS_HASH}
                onClick={(e) => handleNavigation(INTEGRATIONS_HASH, e)}
                className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
              >
                Integrations
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
              </a>

              {/* Solutions Dropdown */}
              <div
                className="relative"
                onMouseEnter={handleSolutionsMouseEnter}
                onMouseLeave={handleSolutionsMouseLeave}
              >
                <button
                  id="solutions-button"
                  className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative py-2 flex items-center group"
                >
                  Solutions
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-300 ${solutionsHovered ? 'rotate-180' : ''}`} />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
                </button>

                {solutionsHovered && (
                  <div
                    id="solutions-dropdown"
                    className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-[440px] bg-white rounded-2xl shadow-2xl border border-gray-100 py-4 z-50"
                  >
                    <div className="flex flex-col gap-2 px-4">
                      {solutionsItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <a
                            key={index}
                            href={item.href}
                            onClick={(e) => handleSolutionsNavigation(item.href, e)}
                            className="group relative p-4 rounded-xl hover:bg-gradient-to-br hover:from-gray-50 hover:to-[#c4ffa0]/5 transition-all duration-300 border border-transparent hover:border-[#c4ffa0]/30"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-900 text-sm group-hover:text-black transition-colors mb-1">
                                  {item.title}
                                </div>
                                <div className="text-xs text-gray-600 leading-relaxed">{item.description}</div>
                              </div>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Features Dropdown — hidden for now */}
              {false && (
              <div 
                className="relative"
                onMouseEnter={handleFeaturesMouseEnter}
                onMouseLeave={handleFeaturesMouseLeave}
              >
                <button 
                  id="features-button"
                  className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative py-2 flex items-center group"
                >
                  Features
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-300 ${featuresHovered ? 'rotate-180' : ''}`} />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
                </button>
                
                {/* Dropdown Menu */}
                {featuresHovered && (
                  <div 
                    id="features-dropdown"
                    className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-[650px] bg-white rounded-2xl shadow-2xl border border-gray-100 py-4 z-50"
                  >
                    <div className="grid grid-cols-2 gap-2 px-4">
                      {featuresItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <a
                            key={index}
                            href={item.href}
                            onClick={(e) => handleNavigation(item.href, e)}
                            className="group relative p-4 rounded-xl hover:bg-gradient-to-br hover:from-gray-50 hover:to-[#c4ffa0]/5 transition-all duration-300 border border-transparent hover:border-[#c4ffa0]/30"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="font-semibold text-gray-900 text-sm group-hover:text-black transition-colors">
                                    {item.title}
                                  </div>
                                  {item.isNew && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-[#c4ffa0] to-green-400 text-black rounded-full">
                                      New
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-600 leading-relaxed">{item.description}</div>
                              </div>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* For Agencies — hidden for now */}
              {false && (
              <a 
                href="/agencies" 
                onClick={(e) => handleNavigation('/agencies', e)}
                className="relative group py-2 px-3 rounded-lg bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <span className="text-base font-semibold text-white">For Agencies</span>
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#c4ffa0]/20 to-green-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              )}
              
              {/* Blog — hidden for now */}
              {/* <a 
                href="/blog" 
                onClick={(e) => handleNavigation('/blog', e)}
                className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
              >
                Blog
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
              </a> */}

              <a
                href="/kolanut-thesis"
                onClick={(e) => handleNavigation('/kolanut-thesis', e)}
                className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
              >
                Thesis
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
              </a>
              
              {/* Pricing */}
              <a 
                href="/pricing" 
                onClick={(e) => handleNavigation('/pricing', e)}
                className="text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
              >
                Pricing
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
              </a>
            </div>
          </nav>
          </div>

          {/* Mobile menu button - right side on small screens */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2.5 rounded-xl transition-all duration-300 ${
                isMenuOpen 
                  ? 'text-black bg-[#c4ffa0]/20 rotate-180 scale-110' 
                  : 'text-gray-700 hover:text-black hover:bg-[#c4ffa0]/20 hover:scale-105'
              }`}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Spacer: pushes CTAs to the far right on desktop */}
          <div className="hidden md:block flex-1 min-w-0" aria-hidden="true" />

          {/* Sign In & Sign Up - far right on desktop */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <a
              href="https://engagement.kolanut.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-semibold text-gray-700 hover:text-black transition-all duration-300 px-3 py-2 rounded-lg hover:bg-[#c4ffa0]/20"
            >
              Sign In
            </a>
            <a 
              href="https://engagement.kolanut.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl"
            >
              <div className="absolute inset-0 bg-[#8ae690] opacity-0 group-hover:opacity-100 transition-all duration-500 blur-md"></div>
              <button className="relative w-full sm:w-auto bg-[#8ae690] hover:bg-[#7ad67f] text-black text-base px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center group-hover:shadow-[#8ae690]/25">
                {/* <Calendar className="w-4 h-4 mr-2 text-black transition-transform duration-300 group-hover:rotate-12" /> */}
                <span>Get Started</span>
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </button>
            </a>
          </div>
        </div>

        {/* Enhanced Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-[#c4ffa0]/20 shadow-xl animate-slideDown">
            <div className="px-4 pt-4 pb-6 space-y-2">
            
              <a 
                href={HOW_IT_WORKS_HASH}
                onClick={(e) => handleNavigation(HOW_IT_WORKS_HASH, e)}
                className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:text-black hover:bg-gradient-to-r hover:from-[#c4ffa0]/10 hover:to-[#c4ffa0]/5 transition-all duration-300 relative group"
              >
                <span className="relative z-10">How it works</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </a>

              <a
                href={INTEGRATIONS_HASH}
                onClick={(e) => handleNavigation(INTEGRATIONS_HASH, e)}
                className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:text-black hover:bg-gradient-to-r hover:from-[#c4ffa0]/10 hover:to-[#c4ffa0]/5 transition-all duration-300 relative group"
              >
                <span className="relative z-10">Integrations</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </a>

              {/* Solutions Mobile Menu */}
              <div className="px-4 py-3">
                <button
                  onClick={handleMobileSolutionsToggle}
                  className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
                >
                  <span>Solutions</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${mobileSolutionsOpen ? 'rotate-180' : ''}`} />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                </button>

                {mobileSolutionsOpen && (
                  <div className="mt-3 ml-2 animate-slideDown">
                    <div className="grid grid-cols-1 gap-2">
                      {solutionsItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <a
                            key={index}
                            href={item.href}
                            onClick={(e) => handleSolutionsNavigation(item.href, e)}
                            className="group flex items-start gap-3 p-3 rounded-lg hover:bg-gradient-to-br hover:from-gray-50 hover:to-[#c4ffa0]/5 transition-all duration-200 border border-transparent hover:border-[#c4ffa0]/30"
                          >
                            <div className={`flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-md`}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 text-sm group-hover:text-black transition-colors mb-1">
                                {item.title}
                              </div>
                              <div className="text-xs text-gray-600">{item.description}</div>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Features Mobile Menu — hidden for now */}
              {false && (
              <div className="px-4 py-3">
                <button
                  onClick={handleMobileFeaturesToggle}
                  className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-black transition-all duration-300 relative group py-2"
                >
                  <span>Features</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${mobileFeaturesOpen ? 'rotate-180' : ''}`} />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                </button>
                
                {/* Mobile Features Dropdown */}
                {mobileFeaturesOpen && (
                  <div className="mt-3 ml-2 animate-slideDown">
                    <div className="grid grid-cols-1 gap-2">
                      {featuresItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <a
                            key={index}
                            href={item.href}
                            onClick={(e) => {
                              handleNavigation(item.href, e);
                              setMobileFeaturesOpen(false);
                            }}
                            className="group flex items-start gap-3 p-3 rounded-lg hover:bg-gradient-to-br hover:from-gray-50 hover:to-[#c4ffa0]/5 transition-all duration-200 border border-transparent hover:border-[#c4ffa0]/30"
                          >
                            <div className={`flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-md`}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="font-semibold text-gray-900 text-sm group-hover:text-black transition-colors">
                                  {item.title}
                                </div>
                                {item.isNew && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-[#c4ffa0] to-green-400 text-black rounded-full">
                                    New
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600">{item.description}</div>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* For Agencies Mobile — hidden for now */}
              {false && (
              <div className="px-4 py-3">
                <a 
                  href="/agencies" 
                  onClick={(e) => handleNavigation('/agencies', e)}
                  className="block px-4 py-3 rounded-xl bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl relative group"
                >
                  <span className="text-base font-semibold text-white">For Agencies</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/20 to-green-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                </a>
              </div>
              )}
              
              {/* Blog — hidden for now */}
              {/* <a 
                href="/blog" 
                onClick={(e) => handleNavigation('/blog', e)}
                className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:text-black hover:bg-gradient-to-r hover:from-[#c4ffa0]/10 hover:to-[#c4ffa0]/5 transition-all duration-300 relative group"
              >
                <span className="relative z-10">Blog</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </a> */}

              <a
                href="/kolanut-thesis"
                onClick={(e) => handleNavigation('/kolanut-thesis', e)}
                className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:text-black hover:bg-gradient-to-r hover:from-[#c4ffa0]/10 hover:to-[#c4ffa0]/5 transition-all duration-300 relative group"
              >
                <span className="relative z-10">Thesis</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </a>
              
              {/* Pricing */}
              <a 
                href="/pricing" 
                onClick={(e) => handleNavigation('/pricing', e)}
                className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:text-black hover:bg-gradient-to-r hover:from-[#c4ffa0]/10 hover:to-[#c4ffa0]/5 transition-all duration-300 relative group"
              >
                <span className="relative z-10">Pricing</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </a>
              
              <div className="pt-6 px-2 space-y-3">
                <a
                  href="https://engagement.kolanut.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center text-base font-semibold text-gray-700 hover:text-black transition-all duration-300 px-4 py-3 rounded-xl hover:bg-[#c4ffa0]/15"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </a>
                <a 
                  href="https://engagement.kolanut.ai/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full group relative overflow-hidden rounded-xl"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0] to-green-400 opacity-0 group-hover:opacity-20 transition-all duration-500 blur-sm"></div>
                  <button className="relative w-full bg-[#8ae690] hover:bg-[#7ad67f] text-black text-base px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center group-hover:scale-[1.02]">
                    {/* <Calendar className="w-5 h-5 mr-2 text-black transition-transform duration-300 group-hover:rotate-12" /> */}
                    <span>Get Started</span>
                    <Sparkles className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 text-black" />
                  </button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </header>
  );
};

export default Header;